import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';

import {
  buildTransfereeCandidates,
  getAvailableTransfereesForInvestor,
} from '@/components/fund-detail/investorTransferUtils';
import { TransfereeSelect } from '@/components/fund-detail/TransfereeSelect';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { CurrencyInput } from '@/components/ui/currency-input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import {
  useLpTransferEditImpact,
  useUpdateLpTransferDraft,
} from '@/hooks/react-query/useFundTransactions';
import {
  useAvailableInvestorsForFund,
  useFundInvestors,
} from '@/hooks/react-query/useInvestors';
import type {
  FundTransactionDetail,
  LpTransferSnapshot,
} from '@/types/fund-transactions';
import type { LpTransferDraftChange } from '@/types/investor';
import { MAX_TRANSFER_TRANSACTION_NAME_LENGTH } from '@/types/investor-transfer';
import { formatCurrency } from '@/utils/formatters';

import { LpTransferImpactSummary } from './LpTransferImpactSummary';

const AMOUNT_DEBOUNCE_MS = 400;

interface DraftForm {
  transactionName: string;
  effectiveDate: string;
  transfereeInvestorId: string;
  amount: number | null;
}

// Recording writes this description when no name is given; prefilling it
// would pin a stale name once the transferee changes.
function defaultDescription(snapshot: LpTransferSnapshot) {
  return `Investor transfer: ${snapshot.transferor_name} to ${snapshot.transferee_name}`;
}

function initialForm(
  transaction: FundTransactionDetail,
  snapshot: LpTransferSnapshot
): DraftForm {
  const description = transaction.description ?? '';
  return {
    transactionName:
      description === defaultDescription(snapshot) ? '' : description,
    effectiveDate: transaction.transaction_date,
    transfereeInvestorId: snapshot.transferee_investor_id,
    amount: transaction.amount,
  };
}

function validate(form: DraftForm, maxAmount: number | null): string | null {
  if (form.transactionName.trim().length > MAX_TRANSFER_TRANSACTION_NAME_LENGTH)
    return `Transaction name cannot exceed ${MAX_TRANSFER_TRANSACTION_NAME_LENGTH} characters.`;
  if (!form.effectiveDate) return 'Effective date is required.';
  if (!form.transfereeInvestorId) return 'Select a transferee investor.';
  if (form.amount == null || form.amount <= 0)
    return 'Transfer commitment must be greater than 0.';
  if (maxAmount != null && form.amount > maxAmount)
    return 'Transfer commitment cannot exceed the transferor commitment.';
  return null;
}

function toDraftChange(form: DraftForm): LpTransferDraftChange | null {
  if (form.amount == null) return null;
  return {
    effective_date: form.effectiveDate,
    transferee_investor_id: form.transfereeInvestorId,
    transfer_commitment_amount: form.amount.toFixed(2),
  };
}

export function EditLpTransferDialog({
  open,
  onOpenChange,
  fundId,
  transaction,
  snapshot,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fundId: string;
  transaction: FundTransactionDetail;
  snapshot: LpTransferSnapshot;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit draft transfer</DialogTitle>
          <DialogDescription className="sr-only">
            Change the draft transfer before it is approved.
          </DialogDescription>
        </DialogHeader>
        <EditDraftForm
          fundId={fundId}
          transaction={transaction}
          snapshot={snapshot}
          onClose={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}

// Mounted only while the dialog is open, so each opening starts from the
// saved transfer.
function EditDraftForm({
  fundId,
  transaction,
  snapshot,
  onClose,
}: {
  fundId: string;
  transaction: FundTransactionDetail;
  snapshot: LpTransferSnapshot;
  onClose: () => void;
}) {
  const [form, setForm] = useState(() => initialForm(transaction, snapshot));

  const fundInvestors = useFundInvestors(fundId);
  const investorsOutsideFund = useAvailableInvestorsForFund(fundId, 'include');
  const updateDraft = useUpdateLpTransferDraft(fundId, transaction.id);

  const currency = transaction.currency;
  const maxAmount =
    snapshot.transferor_commitment_before != null
      ? Number(snapshot.transferor_commitment_before)
      : null;

  const debouncedAmount = useDebouncedValue(form.amount, AMOUNT_DEBOUNCE_MS);
  const isAmountSettled = debouncedAmount === form.amount;
  const validationError = validate(form, maxAmount);
  const change = validationError ? null : toDraftChange(form);
  const impact = useLpTransferEditImpact(
    fundId,
    transaction.id,
    isAmountSettled ? change : null
  );

  const transferees = getAvailableTransfereesForInvestor(
    snapshot.transferor_investor_id,
    buildTransfereeCandidates(
      fundInvestors.data ?? [],
      investorsOutsideFund.data ?? []
    )
  );
  const selectedTransfereeName =
    transferees.find((candidate) => candidate.id === form.transfereeInvestorId)
      ?.name ??
    (form.transfereeInvestorId === snapshot.transferee_investor_id
      ? snapshot.transferee_name
      : undefined);

  const isSaving = updateDraft.isPending;
  const canSave =
    change != null &&
    isAmountSettled &&
    impact.data?.blockers.length === 0 &&
    !impact.isFetching &&
    !isSaving;

  const updateForm = (patch: Partial<DraftForm>) => {
    setForm((previous) => ({ ...previous, ...patch }));
    updateDraft.reset();
  };

  const handleSave = () => {
    if (!canSave || !change) return;
    updateDraft.mutate(
      { ...change, transaction_name: form.transactionName.trim() || null },
      { onSuccess: onClose }
    );
  };

  return (
    <>
      <div className="space-y-4">
        {updateDraft.error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <span>{updateDraft.error.message}</span>
          </Alert>
        )}

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="edit_transfer_transaction_name">
              Transaction Name
            </Label>
            <Input
              id="edit_transfer_transaction_name"
              value={form.transactionName}
              onChange={(event) =>
                updateForm({ transactionName: event.target.value })
              }
              maxLength={MAX_TRANSFER_TRANSACTION_NAME_LENGTH}
              placeholder="Transfer name"
              disabled={isSaving}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit_transfer_effective_date">Date</Label>
            <Input
              id="edit_transfer_effective_date"
              type="date"
              value={form.effectiveDate}
              onChange={(event) =>
                updateForm({ effectiveDate: event.target.value })
              }
              disabled={isSaving}
            />
          </div>

          <div className="space-y-2">
            <Label>Transferor</Label>
            <p className="text-sm" data-testid="edit-transfer-transferor">
              {snapshot.transferor_name}
            </p>
            {maxAmount != null && (
              <p className="text-xs text-muted-foreground">
                Commitment before the transfer:{' '}
                {formatCurrency(maxAmount, { currency })}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit_transfer_transferee">Transferee *</Label>
            <TransfereeSelect
              id="edit_transfer_transferee"
              candidates={transferees}
              onSelect={(candidate) =>
                updateForm({ transfereeInvestorId: candidate.id })
              }
              selectedKey={form.transfereeInvestorId}
              triggerLabel={selectedTransfereeName}
              isLoading={
                fundInvestors.isLoading || investorsOutsideFund.isLoading
              }
              disabled={isSaving}
              placeholder="Select transferee investor"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit_transfer_commitment">
              Transfer Commitment *
            </Label>
            <CurrencyInput
              id="edit_transfer_commitment"
              value={form.amount}
              onChange={(amount) => updateForm({ amount })}
              max={maxAmount ?? undefined}
              currency={currency}
              placeholder="Enter amount"
              disabled={isSaving}
            />
          </div>
        </div>

        {validationError ? (
          <p className="text-sm text-destructive">{validationError}</p>
        ) : (
          <LpTransferImpactSummary fundId={fundId} query={impact} />
        )}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={!canSave}>
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </DialogFooter>
    </>
  );
}
